import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useSearchParams } from "react-router-dom";
import PageTopBar from "../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../components/common/toolbarStyles";
import ClassPicker from "../components/ClassPicker";
import TemplateParagraph from "../components/TemplateParagraph";
import { getAlertTemplates } from "../mockTemplates";
import { AlertRecipients, AlertTemplate, AlertType } from "../types";

// Sending needs the backend (POST /alerts { templateId, recipients: "ALL" | [{ grade, section }], variables }), not built yet.
const SENDING_AVAILABLE = false;

const LOOK: Record<AlertType, { icon: React.ReactNode; accent: string; description: string }> = {
  HOLIDAY: { icon: <BeachAccessIcon />, accent: "warning", description: "Tell guardians the school is closed." },
  EVENT: { icon: <CelebrationIcon />, accent: "primary", description: "Tell guardians about a school event." },
  FEE_REMINDER: { icon: <CurrencyRupeeIcon />, accent: "error", description: "Remind guardians of a class about outstanding fees." },
};
const DEFAULT_LOOK = { icon: <CampaignIcon />, accent: "info", description: "" };

/**
 * Notifications → Send: one page for every kind of notification. The Type picks a DLT template; the
 * template decides the rest - whether "All classes" is offered, dues-only recipients, and which words the
 * admin fills in (user variables) vs. the backend fills per recipient (system variables).
 */
export default function SendAlertPage() {
  const [templates, setTemplates] = useState<AlertTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [values, setValues] = useState<Record<string, string>>({});
  const [recipients, setRecipients] = useState<AlertRecipients>("ALL");

  useEffect(() => {
    getAlertTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
  }, []);

  // ?type=HOLIDAY keeps the choice on refresh / back; defaults to the first template
  const template = useMemo(
    () => templates.find((t) => t.type === params.get("type")) ?? templates[0],
    [templates, params]
  );

  // each template starts from its own default audience: All classes where offered, otherwise none
  useEffect(() => {
    if (template) setRecipients(template.allowAll ? "ALL" : []);
  }, [template]);

  const chooseType = (type: string) => {
    setParams({ type }, { replace: true });
    setValues({}); // variables differ per template
  };

  const userVariables = template?.variables.filter((v) => v.source === "user") ?? [];
  const hasRecipients = recipients === "ALL" || recipients.length > 0;
  const complete = userVariables.every((v) => (values[v.key] ?? "").trim().length > 0) && hasRecipients;
  const canSend = SENDING_AVAILABLE && Boolean(template) && complete;

  const look = template ? LOOK[template.type] ?? DEFAULT_LOOK : DEFAULT_LOOK;
  const whom =
    recipients === "ALL"
      ? "all classes"
      : `${recipients.length} selected class${recipients.length === 1 ? "" : "es"}`;
  const audienceText = !template
    ? ""
    : !hasRecipients
      ? "Select at least one class."
      : template.onlyWithDues
        ? `Goes to guardians in ${whom} whose child has outstanding dues; each message is filled in for that student.`
        : `Goes to guardians in ${whom}.`;

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left="Send Notification"
        right={
          template && (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={toolbarFieldsSx}>
              {/* Type first: it decides which other inputs appear */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="alert-type">Type</InputLabel>
                <Select labelId="alert-type" label="Type" value={template.type} onChange={(e) => chooseType(e.target.value)}>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.type}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ClassPicker value={recipients} onChange={setRecipients} allowAll={template.allowAll} buttonSx={toolbarButtonSx} />
            </Box>
          )
        }
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : !template ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No alert templates are set up yet.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ maxWidth: 720, mx: "auto", mt: { xs: 1, sm: 3 }, borderRadius: 3, overflow: "hidden" }}>
          {/* header */}
          <Box display="flex" alignItems="center" gap={2} sx={{ px: { xs: 2, sm: 3 }, py: 2.5, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
            <Avatar sx={{ bgcolor: `${look.accent}.light`, color: `${look.accent}.dark`, width: 44, height: 44 }}>{look.icon}</Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
                {template.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {look.description}
              </Typography>
            </Box>
          </Box>

          {/* the message: a normal paragraph with fill-in fields */}
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <TemplateParagraph
              template={template}
              values={values}
              onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
            />
            {userVariables.length === 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Nothing to fill in — the grey parts are filled in for each student when the message is sent.
              </Typography>
            )}
          </Box>

          {/* footer */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1.5}
            sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: "divider" }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                {audienceText}
              </Typography>
              {!SENDING_AVAILABLE && (
                <Typography variant="caption" color="text.secondary">
                  SMS sending isn't available yet.
                </Typography>
              )}
            </Box>
            <Box flexGrow={1} />
            <Tooltip title={SENDING_AVAILABLE ? (complete ? "" : "Fill in every field first") : "Sending is not available yet"}>
              <span>
                <Button variant="contained" startIcon={<SendIcon />} disabled={!canSend} sx={{ width: { xs: "100%", sm: "auto" }, px: 3 }}>
                  Send
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
